import { useEffect, type RefObject } from 'react';

import type { Locale } from '@kilat/shared';
import type { HelpSlug } from './articles.js';

/**
 * Schema.org structured data for the operator manual (ADR-0024 slice 2b part B).
 *
 * Every `/help/{slug}` article emits a `TechArticle` JSON-LD block (headline,
 * dateModified, language, publisher). The genuinely Q&A-structured `faq`
 * article ALSO emits a `FAQPage` block, built from its rendered `## question`
 * → answer pairs — the shape Google's FAQ rich result requires. We scope
 * `FAQPage` to real Q&A content on purpose: emitting it over prose articles
 * would be mismatched structured data (a manual-action risk), so the other
 * articles get `TechArticle` only.
 *
 * The pure builders below are unit-tested; the thin DOM reader + injection hook
 * run in the browser (both at runtime and inside the build-time prerender, so
 * the JSON-LD lands in the STATIC snapshot).
 */

/** BCP-47 language tag for the `inLanguage` field. */
export function localeToBcp47(locale: Locale): string {
  switch (locale) {
    case 'zh':
      return 'zh-Hans';
    default:
      return locale;
  }
}

export interface TechArticleArgs {
  readonly title: string;
  /** ISO date (YYYY-MM-DD) from the article's `meta.updatedAt`. */
  readonly updatedAt: string;
  readonly inLanguage: string;
  readonly url: string;
  readonly brandName: string;
}

export function buildTechArticleJsonLd(args: TechArticleArgs): Record<string, unknown> {
  const publisher = { '@type': 'Organization', name: args.brandName };
  return {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: args.title,
    inLanguage: args.inLanguage,
    dateModified: args.updatedAt,
    author: publisher,
    publisher,
    url: args.url,
    mainEntityOfPage: { '@type': 'WebPage', '@id': args.url },
  };
}

export interface FaqPair {
  readonly question: string;
  readonly answer: string;
}

export function buildFaqPageJsonLd(
  pairs: readonly FaqPair[],
  inLanguage: string,
): Record<string, unknown> | null {
  if (pairs.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage,
    mainEntity: pairs.map((p) => ({
      '@type': 'Question',
      name: p.question,
      acceptedAnswer: { '@type': 'Answer', text: p.answer },
    })),
  };
}

/** A rendered block reduced to what FAQ pairing needs (pure-testable). */
export interface FaqNode {
  readonly tag: string;
  readonly text: string;
}

/**
 * Pair each `h2` (a `## question`) with the text of the following blocks up to
 * the next `h2`. Any lead-in prose before the first heading is ignored. Returns
 * only complete question+answer pairs.
 */
export function pairsFromNodes(nodes: readonly FaqNode[]): FaqPair[] {
  const pairs: FaqPair[] = [];
  let question: string | null = null;
  let answer = '';
  const flush = () => {
    if (question && answer.trim()) pairs.push({ question, answer: answer.trim() });
  };
  for (const node of nodes) {
    if (node.tag === 'h2') {
      flush();
      question = node.text.trim() || null;
      answer = '';
    } else if (question) {
      const text = node.text.trim();
      if (text) answer += (answer ? ' ' : '') + text;
    }
  }
  flush();
  return pairs;
}

/** DOM adapter over {@link pairsFromNodes} for a rendered article body. */
export function extractFaqPairs(root: Element): FaqPair[] {
  return pairsFromNodes(
    Array.from(root.children).map((el) => ({
      tag: el.tagName.toLowerCase(),
      text: el.textContent ?? '',
    })),
  );
}

const JSONLD_MARKER = 'data-help-jsonld';

function injectJsonLd(blocks: readonly Record<string, unknown>[]): void {
  const head = document.head;
  head.querySelectorAll(`script[${JSONLD_MARKER}]`).forEach((el) => el.remove());
  for (const block of blocks) {
    const script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute(JSONLD_MARKER, '');
    script.textContent = JSON.stringify(block);
    head.appendChild(script);
  }
}

export interface UseHelpStructuredDataArgs {
  readonly slug: HelpSlug;
  readonly ready: boolean;
  readonly title: string | null;
  readonly updatedAt: string | null;
  readonly localeUsed: Locale | null;
  readonly brandName: string;
  readonly bodyRef: RefObject<HTMLElement>;
}

/**
 * Inject the article's JSON-LD into `<head>` once its body has rendered, and
 * clear it on navigation/unmount. Depends on the canonical URL that
 * `useMarketingSeo` sets in the same render pass (called before this hook in
 * `ArticleBody`), falling back to `location.href`.
 */
export function useHelpStructuredData({
  slug,
  ready,
  title,
  updatedAt,
  localeUsed,
  brandName,
  bodyRef,
}: UseHelpStructuredDataArgs): void {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!ready || !title || !updatedAt || !localeUsed) return;

    const inLanguage = localeToBcp47(localeUsed);
    const url =
      document.querySelector('link[rel="canonical"]')?.getAttribute('href') ?? window.location.href;

    const blocks: Record<string, unknown>[] = [
      buildTechArticleJsonLd({ title, updatedAt, inLanguage, url, brandName }),
    ];
    if (slug === 'faq' && bodyRef.current) {
      const faq = buildFaqPageJsonLd(extractFaqPairs(bodyRef.current), inLanguage);
      if (faq) blocks.push(faq);
    }
    injectJsonLd(blocks);

    return () => {
      document.head.querySelectorAll(`script[${JSONLD_MARKER}]`).forEach((el) => el.remove());
    };
  }, [slug, ready, title, updatedAt, localeUsed, brandName, bodyRef]);
}
